/**
 * Super Admin Workspace Detail - 개별 워크스페이스 상세 및 구독 관리
 * 
 * - 워크스페이스 정보 (Overview)
 * - 구독 관리 (Subscription History)
 * - 결제 정보 (Billing Profile)
 * - 청구서 발행 (Documents)
 * - 사용량 통계 (Usage)
 * 
 * 각 탭은 별도 컴포넌트로 분리되어 필요 시에만 데이터를 로딩합니다.
 */

import { Trans } from '@lingui/macro';
import {
    ArrowLeft, Building2, CreditCard, FileText,
    BarChart3, Loader2, LayoutDashboard
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Fire } from '../../services/FireClient';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';

// Tab Components
import WorkspaceOverviewTab from './tabs/WorkspaceOverviewTab';
import WorkspaceSubscriptionTab from './tabs/WorkspaceSubscriptionTab';
import WorkspaceCompanyTab from './tabs/WorkspaceCompanyTab';
import WorkspaceDocumentsTab from './tabs/WorkspaceDocumentsTab';
import WorkspaceUsageTab from './tabs/WorkspaceUsageTab';

interface WorkspaceBasicInfo {
    id: string;
    name: string;
    memberCount: number;
    storageMB: number;
    tier: string;
    createdAt: Date;
    owner?: { id: string; name: string; email: string };
}

const SuperAdminWorkspaceDetail: React.FC = () => {
    const navigate = useNavigate();
    const { workspaceId } = useParams<{ workspaceId: string }>();

    // Loading state
    const [loading, setLoading] = useState(true);
    const [workspace, setWorkspace] = useState<WorkspaceBasicInfo | null>(null);

    // Refresh key to force tab re-renders when data changes
    const [refreshKey, setRefreshKey] = useState(0);

    // Load basic workspace info on mount
    useEffect(() => {
        if (workspaceId) {
            loadWorkspaceBasicInfo();
        }
    }, [workspaceId]);

    const loadWorkspaceBasicInfo = async () => {
        if (!workspaceId) return;
        setLoading(true);

        try {
            // Load workspace basic info
            const wsDoc = await getDoc(doc(Fire.store, 'workspaces', workspaceId));
            if (wsDoc.exists()) {
                const wsData = wsDoc.data();
                const membersSnap = await getDocs(collection(Fire.store, 'workspaces', workspaceId, 'members'));

                setWorkspace({
                    id: workspaceId,
                    name: wsData.name || 'Unknown',
                    memberCount: membersSnap.size,
                    storageMB: 0,
                    tier: wsData.tier || 'FREE',
                    createdAt: wsData.created?.toDate?.() || new Date(),
                    owner: wsData.owner,
                });
            }
        } catch (error) {
            console.error('Failed to load workspace:', error);
        } finally {
            setLoading(false);
        }
    };

    // Called when subscription is created/updated
    const handleSubscriptionChanged = () => {
        setRefreshKey(prev => prev + 1);
        loadWorkspaceBasicInfo(); // Refresh workspace.tier in header
    };

    // Loading state
    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
        );
    }

    // Workspace not found
    if (!workspace) {
        return (
            <div className="flex flex-col items-center justify-center h-full">
                <p className="text-gray-500 mb-4"><Trans>Workspace not found</Trans></p>
                <Button variant="outline" onClick={() => navigate('/super-admin/workspaces')}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    <Trans>Back to list</Trans>
                </Button>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="bg-white/80 backdrop-blur-md border-b border-slate-100/50 px-6 py-4">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/super-admin/workspaces')}>
                        <ArrowLeft className="w-4 h-4" />
                    </Button>
                    <div className="flex-1">
                        <div className="flex items-center gap-3">
                            <h1 className="text-xl font-bold text-slate-900">{workspace.name}</h1>
                            <Badge className="bg-indigo-100 text-indigo-700">{workspace.tier}</Badge>
                        </div>
                        <p className="text-sm text-slate-500 mt-0.5">ID: {workspace.id}</p>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6 bg-slate-50/30">
                <Tabs defaultValue="overview" className="space-y-6">
                    <TabsList className="bg-white border border-slate-200">
                        <TabsTrigger value="overview" className="data-[state=active]:bg-indigo-50">
                            <LayoutDashboard className="w-4 h-4 mr-2" />
                            <Trans>Overview</Trans>
                        </TabsTrigger>
                        <TabsTrigger value="subscription" className="data-[state=active]:bg-indigo-50">
                            <CreditCard className="w-4 h-4 mr-2" />
                            <Trans>Subscription</Trans>
                        </TabsTrigger>
                        <TabsTrigger value="company" className="data-[state=active]:bg-indigo-50">
                            <Building2 className="w-4 h-4 mr-2" />
                            회사정보
                        </TabsTrigger>
                        <TabsTrigger value="documents" className="data-[state=active]:bg-indigo-50">
                            <FileText className="w-4 h-4 mr-2" />
                            <Trans>Documents</Trans>
                        </TabsTrigger>
                        <TabsTrigger value="usage" className="data-[state=active]:bg-indigo-50">
                            <BarChart3 className="w-4 h-4 mr-2" />
                            <Trans>Usage</Trans>
                        </TabsTrigger>
                    </TabsList>

                    {/* Overview Tab */}
                    <TabsContent value="overview">
                        <WorkspaceOverviewTab key={`overview-${refreshKey}`} workspaceId={workspaceId!} workspace={workspace} />
                    </TabsContent>

                    {/* Subscription Tab */}
                    <TabsContent value="subscription">
                        <WorkspaceSubscriptionTab workspaceId={workspaceId!} onSubscriptionChanged={handleSubscriptionChanged} />
                    </TabsContent>

                    {/* Company Info Tab */}
                    <TabsContent value="company">
                        <WorkspaceCompanyTab workspaceId={workspaceId!} />
                    </TabsContent>

                    {/* Documents Tab */}
                    <TabsContent value="documents">
                        <WorkspaceDocumentsTab workspaceId={workspaceId!} />
                    </TabsContent>

                    {/* Usage Tab */}
                    <TabsContent value="usage">
                        <WorkspaceUsageTab workspaceId={workspaceId!} />
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
};

export default SuperAdminWorkspaceDetail;
